import React from "react";
import clsx from "clsx";
import { useScreenSize } from "~/utils/layout";
import { Arrow } from "~/components/icons/arrow";
import { ChevronIcon } from "~/components/icons/chevron";

import * as styles from "./index.module.scss";

/**
 * A versatile button component that supports multiple layouts and icon configurations.
 * @typedef {Object} ButtonProps
 * @property {(event: React.MouseEvent<HTMLButtonElement>) => void} [onClick] - Click handler function
 * @property {string} [label] - Button text label
 * @property {React.ReactNode} [children] - Button child elements
 * @property {string} [className] - Additional CSS class names
 * @property {'primary' | 'secondary' | 'tertiary'} [layout='primary'] - Button style variant
 * @property {'' | 'left' | 'right' | 'down'} [withChevron=''] - Chevron icon position
 * @property {boolean} [false] - Small button variant
 * @property {boolean} [false] - Big button variant
 * @property {React.ComponentType<any>} [Icon] - Custom icon component
 * @property {string} [viewBox] - SVG viewBox for icons
 * @property {boolean} [useChevron=false] - Force using chevron instead of arrow
 */

/**
 * Button Component
 * @type {React.ForwardRefExoticComponent<ButtonProps & React.RefAttributes<HTMLButtonElement>>}
 */
export const Button = React.forwardRef(
  (
    {
      onClick,
      label,
      children,
      className,
      layout = "primary",
      withChevron = "",
      small = false,
      big = false,
      Icon,
      viewBox,
      useChevron = false,
      ...rest
    },
    ref,
  ) => {
    const isMobileSize = useScreenSize(["mini", "small"]);
    const ArrowIcon = isMobileSize || useChevron ? ChevronIcon : Arrow;

    const buttonClasses = clsx(
      styles.button,
      styles[layout],
      {
        [styles.small]: small,
        [styles.big]: big,
      },
      className,
    );

    return (
      <button
        type="button"
        onClick={onClick}
        className={buttonClasses}
        ref={ref}
        {...rest}
      >
        {withChevron === "left" && (
          <ArrowIcon
            direction="right"
            small
            viewBox={viewBox}
            className={clsx(styles.chevron, styles.chevronLeft)}
          />
        )}
        {label || children}
        {withChevron === "right" && (
          <ArrowIcon
            direction="right"
            small
            className={clsx(styles.chevron, styles.chevronRight)}
          />
        )}
        {withChevron === "down" && (
          <ChevronIcon
            direction="down"
            small
            viewBox={viewBox}
            className={styles.chevron}
          />
        )}
        {Icon && <Icon />}
      </button>
    );
  },
);

Button.displayName = "Button";
